import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, Send, Check, Loader2 } from 'lucide-react';
import { ActionProposalContext } from '../SuperAdmin/ActionProposalCard.jsx';

/**
 * DealerReportCard — 經銷商診斷「回報給 Qubby」確認卡
 *
 * dealer AI 判為系統端問題（或灰色案例）時，輸出 ```dealer-report fenced JSON
 * { summary, kind, ref, internal }；MarkdownText 偵測後渲染本卡片。
 * 按「回報給 Qubby」→ 透過 ActionProposalContext.sendMessage 把 `[report-confirmed]`
 * 回灌對話，AI 端才呼叫 file-ticket.js 開單並回 TK 編號（不開新端點、沿用 SSE chat）。
 *
 * 版型：max-w-full + break-words + 不顯示 internal/ref，確保不溢出對話框。
 */
export function DealerReportCard({ raw }) {
    const { t } = useTranslation('dealer');
    const ctx = useContext(ActionProposalContext);
    const [sent, setSent] = useState(false);

    let p = null;
    try { p = JSON.parse(raw); } catch { /* 非法 JSON → 原樣顯示 */ }
    if (!p) return <pre className="my-2 p-3 rounded-md bg-cream-100 text-xs overflow-x-auto">{raw}</pre>;

    const send = () => {
        if (!ctx || sent) return;
        setSent(true);
        ctx.sendMessage?.(
            '[report-confirmed] 經銷商已按下按鈕確認回報，請呼叫 file-ticket.js 開立工單並回覆工單編號。',
            { displayText: t('report.sent') },
        );
    };

    return (
        <div className="my-3 rounded-lg border border-accent-300 bg-accent-50/40 p-3 max-w-full overflow-hidden whitespace-normal font-sans">
            <div className="flex items-start gap-2.5">
                <Flag size={16} className="text-accent-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink-900">{t('report.title')}</div>
                    {p.summary && <p className="text-xs text-ink-600 mt-1 leading-relaxed break-words">{p.summary}</p>}
                    <div className="mt-2.5">
                        {sent ? (
                            <span className="text-sm text-success-600 inline-flex items-center gap-1.5">
                                <Check size={14} /> {t('report.sentDone')}
                            </span>
                        ) : (
                            <button
                                onClick={send}
                                disabled={!ctx}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-accent-500 text-cream-50 text-sm font-medium hover:bg-accent-700 disabled:opacity-50 transition"
                            >
                                <Send size={14} /> {t('report.button')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
